import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { ChevronLeft, ChevronRight, Layers, Minus, Plus, RotateCcw, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDocumentPages } from '../../../hooks/useDocumentPages';
import { usePortal } from '../../../hooks/usePortal';
import { BlockTree } from './BlockTree';
import { PageCanvas } from './PageCanvas';
const ZOOM_LEVELS = [
    0.5,
    0.75,
    1,
    1.25,
    1.5,
    2,
    3
];
function normalizeBlockId(blockId) {
    if (!blockId) return null;
    const id = blockId.replace(/^coalesced-/, '').trim();
    if (!id) return null;
    return id;
}
/**
 * Match a source block ID against the current page's block IDs.
 *
 * zou's blocks.json and the page-visualization endpoint emit the same
 * `/page/N/Type/i` format, so an exact string match is sufficient and
 * correct. We deliberately do NOT do any suffix or substring fallback:
 * the intra-page index (`SectionHeader/6`, `Text/3`, ...) repeats on
 * every page, so any suffix-based fallback cross-matches unrelated
 * blocks across pages and bleeds the PWS highlight into pages outside
 * the section grouping.
 *
 * When a member block lives on a different page, this returns null —
 * which is the correct outcome. As soon as the user navigates to that
 * page, this same function will resolve the source ID exactly there.
 */ function findMatchingBlockId(sourceBlockId, pageBlocks) {
    if (!sourceBlockId || pageBlocks.length === 0) return null;
    const exact = pageBlocks.find((b)=>b.id === sourceBlockId);
    return exact ? exact.id : null;
}
/**
 * Document Viewer — right-side drawer with PDF page rendering and
 * block bounding box highlights.
 *
 * Slides in from the right at ~60% viewport width. The highlighted block
 * draws the eye with an amber glow. Controls are large and accessible
 * (WCAG 44px touch targets).
 *
 * Keyboard: Arrow keys for pages, +/- for zoom, Escape to close.
 */ export const DocumentViewerDrawer = ({ isOpen, onClose, projectId, document, highlightBlockId, highlightBlockIds, initialPage, initialZoom = 1, mode = 'standalone', parentDrawerWidth = 768, pageLoader, 'data-testid': dataTestId })=>{
    const portalContainer = usePortal('document-viewer-portal');
    const [currentPage, setCurrentPage] = useState(1);
    const [viewport, setViewport] = useState({
        zoom: initialZoom,
        panX: 0,
        panY: 0
    });
    const [selectedBlockId, setSelectedBlockId] = useState(null);
    const [showStructure, setShowStructure] = useState(false);
    const loadPage = pageLoader ?? useDocumentPages;
    const { data: pageData } = loadPage(projectId, document?.documentId ?? '', currentPage);
    const totalPages = pageData?.totalPages || document?.pageCount || 0;
    // Normalized set of "secondary" highlights — every block that belongs to
    // the same PWS section as the primary anchor. Resolved against the actual
    // block IDs on the current page once pageData loads, so cross-format
    // (`coalesced-` prefix, etc.) source IDs still light up the right blocks.
    const highlightedBlockIdSet = useMemo(()=>{
        if (!highlightBlockIds || highlightBlockIds.length === 0) {
            return selectedBlockId ? new Set([
                selectedBlockId
            ]) : new Set();
        }
        const set = new Set();
        if (selectedBlockId) set.add(selectedBlockId);
        // Only resolve against the current page. Source IDs for blocks on
        // other pages don't match and aren't added — they'd just be dead
        // entries cluttering the set, and (if we did any loose suffix match)
        // could cross-page false-positive into unrelated blocks.
        if (!pageData?.blocks) return set;
        for (const id of highlightBlockIds){
            const normalized = normalizeBlockId(id);
            if (!normalized) continue;
            const matched = findMatchingBlockId(normalized, pageData.blocks);
            if (matched) set.add(matched);
        }
        return set;
    }, [
        highlightBlockIds,
        selectedBlockId,
        pageData?.blocks
    ]);
    useEffect(()=>{
        if (document) {
            setCurrentPage(initialPage && initialPage >= 1 ? initialPage : 1);
            setViewport({
                zoom: initialZoom,
                panX: 0,
                panY: 0
            });
            setSelectedBlockId(normalizeBlockId(highlightBlockId));
        }
    }, [
        document?.documentId,
        document,
        initialPage,
        initialZoom,
        highlightBlockId
    ]);
    // Resolve block ID match when page data loads — source block IDs may differ in format
    useEffect(()=>{
        if (pageData?.blocks && highlightBlockId) {
            const normalized = normalizeBlockId(highlightBlockId);
            if (!normalized) return;
            const matched = findMatchingBlockId(normalized, pageData.blocks);
            if (matched && matched !== selectedBlockId) {
                setSelectedBlockId(matched);
            }
        }
    }, [
        pageData?.blocks,
        highlightBlockId,
        selectedBlockId
    ]);
    useEffect(()=>{
        if (!isOpen) {
            setViewport({
                zoom: initialZoom,
                panX: 0,
                panY: 0
            });
            setSelectedBlockId(null);
            setShowStructure(false);
        }
    }, [
        isOpen,
        initialZoom
    ]);
    const zoomIn = useCallback(()=>{
        setViewport((prev)=>{
            const idx = ZOOM_LEVELS.findIndex((l)=>l >= prev.zoom);
            const next = idx < ZOOM_LEVELS.length - 1 ? idx + 1 : idx;
            return {
                ...prev,
                zoom: ZOOM_LEVELS[next]
            };
        });
    }, []);
    const zoomOut = useCallback(()=>{
        setViewport((prev)=>{
            const idx = ZOOM_LEVELS.findIndex((l)=>l >= prev.zoom);
            const next = idx > 0 ? idx - 1 : 0;
            return {
                ...prev,
                zoom: ZOOM_LEVELS[next]
            };
        });
    }, []);
    useEffect(()=>{
        if (!isOpen) return;
        const handler = (e)=>{
            if (e.key === 'Escape') {
                onClose();
                return;
            }
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setCurrentPage((p)=>Math.max(1, p - 1));
            }
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                setCurrentPage((p)=>Math.min(totalPages || p, p + 1));
            }
            if (e.key === '+' || e.key === '=') {
                e.preventDefault();
                zoomIn();
            }
            if (e.key === '-') {
                e.preventDefault();
                zoomOut();
            }
            if (e.key === '0') {
                e.preventDefault();
                setViewport({
                    zoom: 1,
                    panX: 0,
                    panY: 0
                });
            }
            if (e.key === 's' || e.key === 'S') {
                e.preventDefault();
                setShowStructure((v)=>!v);
            }
        };
        window.addEventListener('keydown', handler);
        return ()=>window.removeEventListener('keydown', handler);
    }, [
        isOpen,
        totalPages,
        onClose,
        zoomIn,
        zoomOut
    ]);
    const handlePanChange = useCallback((deltaX, deltaY)=>{
        setViewport((prev)=>({
                ...prev,
                panX: prev.panX + deltaX,
                panY: prev.panY + deltaY
            }));
    }, []);
    if (!isOpen || !document) return null;
    const isExtension = mode === 'extension';
    const drawerContent = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            !isExtension && /*#__PURE__*/ _jsx("div", {
                className: "fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]",
                onClick: onClose,
                role: "presentation"
            }),
            /*#__PURE__*/ _jsxs("div", {
                // The caller's testid when one is passed (the pricing-sheet source viewer
                // names its instance), the shared drawer testid otherwise — ONE attribute.
                // The rebase briefly produced both on this element: our prop-based id and
                // main's static one merged textually clean and duplicated, which JSX
                // rejects. Type-check caught it; this is the semantic merge.
                "data-testid": dataTestId ?? Testids.deliveryShared.documentViewerDrawer,
                className: isExtension ? // sat UNDER the scrim of the drawer it extends: it looked dimmed,
                // every click on it landed on the scrim instead, and Radix read that
                // as an outside interaction and dismissed the drawer — taking this
                // panel with it. Extension mode only makes sense above the scrim.
                'fixed inset-y-0 z-[60] flex flex-col rounded-r-[var(--radius-xl)] bg-background shadow-2xl overflow-visible' : 'fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-slate-200 bg-white shadow-2xl sm:w-[70vw] lg:w-[60vw]',
                style: isExtension ? {
                    left: '80px',
                    right: `${parentDrawerWidth}px`
                } : undefined,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2",
                        children: [
                            /*#__PURE__*/ _jsx(ToolbarButton, {
                                onClick: ()=>setShowStructure((v)=>!v),
                                active: showStructure,
                                label: "Toggle structure panel",
                                shortcut: "S",
                                children: /*#__PURE__*/ _jsx(Layers, {
                                    className: "h-4 w-4"
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0 flex-1 px-1",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "truncate text-sm font-semibold text-slate-900",
                                        "data-testid": Testids.deliveryShared.documentViewerFilename,
                                        children: document.filename
                                    }),
                                    document.documentType && /*#__PURE__*/ _jsx("div", {
                                        className: "text-[11px] text-slate-400",
                                        children: document.documentType
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "hidden items-center gap-1.5 text-[10px] text-slate-400 lg:flex",
                                children: [
                                    /*#__PURE__*/ _jsx("kbd", {
                                        className: "rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono",
                                        children: "←→"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: "pages"
                                    }),
                                    /*#__PURE__*/ _jsx("kbd", {
                                        className: "rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono",
                                        children: "+−"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: "zoom"
                                    }),
                                    /*#__PURE__*/ _jsx("kbd", {
                                        className: "rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono",
                                        children: "esc"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: "close"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(ToolbarButton, {
                                onClick: onClose,
                                label: "Close viewer",
                                shortcut: "Esc",
                                children: /*#__PURE__*/ _jsx(X, {
                                    className: "h-4 w-4"
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative flex flex-1 overflow-hidden",
                        children: [
                            showStructure && /*#__PURE__*/ _jsxs("div", {
                                className: "w-56 flex-shrink-0 overflow-y-auto border-r border-slate-200 bg-slate-50 lg:w-64",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "border-b border-slate-100 px-3 py-2",
                                        children: /*#__PURE__*/ _jsxs("span", {
                                            className: "text-[10px] font-bold uppercase tracking-wider text-slate-400",
                                            children: [
                                                "Blocks on page ",
                                                currentPage
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(BlockTree, {
                                        projectId: projectId,
                                        documentId: document.documentId,
                                        pageNumber: currentPage,
                                        selectedBlockId: selectedBlockId,
                                        onBlockSelect: setSelectedBlockId
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex-1 overflow-hidden",
                                children: /*#__PURE__*/ _jsx(PageCanvas, {
                                    projectId: projectId,
                                    documentId: document.documentId,
                                    pageNumber: currentPage,
                                    viewport: viewport,
                                    selectedBlockId: selectedBlockId,
                                    highlightedBlockIds: highlightedBlockIdSet,
                                    onBlockSelect: setSelectedBlockId,
                                    onPanChange: handlePanChange,
                                    pageLoader: pageLoader
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "absolute bottom-5 left-1/2 z-20 -translate-x-1/2",
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-0.5 rounded-2xl border border-slate-200/80 bg-white px-3 py-2 shadow-xl shadow-slate-900/10",
                                    children: [
                                        /*#__PURE__*/ _jsx(ToolbarButton, {
                                            onClick: ()=>setCurrentPage((p)=>Math.max(1, p - 1)),
                                            disabled: currentPage <= 1,
                                            label: "Previous page",
                                            shortcut: "←",
                                            children: /*#__PURE__*/ _jsx(ChevronLeft, {
                                                className: "h-5 w-5"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mx-1 flex min-w-[90px] flex-col items-center justify-center",
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "text-[15px] font-semibold tabular-nums text-slate-800",
                                                    children: [
                                                        currentPage,
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mx-0.5 text-slate-300",
                                                            children: "/"
                                                        }),
                                                        totalPages
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "text-[10px] text-slate-400",
                                                    children: "pages"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(ToolbarButton, {
                                            onClick: ()=>setCurrentPage((p)=>Math.min(totalPages, p + 1)),
                                            disabled: currentPage >= totalPages,
                                            label: "Next page",
                                            shortcut: "→",
                                            children: /*#__PURE__*/ _jsx(ChevronRight, {
                                                className: "h-5 w-5"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "mx-2 h-8 w-px bg-slate-200"
                                        }),
                                        /*#__PURE__*/ _jsx(ToolbarButton, {
                                            onClick: zoomOut,
                                            disabled: viewport.zoom <= ZOOM_LEVELS[0],
                                            label: "Zoom out",
                                            shortcut: "−",
                                            children: /*#__PURE__*/ _jsx(Minus, {
                                                className: "h-5 w-5"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mx-1 flex min-w-[56px] flex-col items-center justify-center",
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "text-[15px] font-semibold tabular-nums text-slate-800",
                                                    children: [
                                                        Math.round(viewport.zoom * 100),
                                                        "%"
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "text-[10px] text-slate-400",
                                                    children: "zoom"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(ToolbarButton, {
                                            onClick: zoomIn,
                                            disabled: viewport.zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1],
                                            label: "Zoom in",
                                            shortcut: "+",
                                            children: /*#__PURE__*/ _jsx(Plus, {
                                                className: "h-5 w-5"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "mx-2 h-8 w-px bg-slate-200"
                                        }),
                                        /*#__PURE__*/ _jsx(ToolbarButton, {
                                            onClick: ()=>setViewport({
                                                    zoom: 1,
                                                    panX: 0,
                                                    panY: 0
                                                }),
                                            label: "Reset view",
                                            shortcut: "0",
                                            children: /*#__PURE__*/ _jsx(RotateCcw, {
                                                className: "h-4 w-4"
                                            })
                                        })
                                    ]
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
    // Standalone mode: portal to body to escape any transformed ancestors.
    // Extension mode: render directly — parent drawers are already portaled,
    // so the fixed positioning works relative to the viewport without a second portal.
    if (!isExtension && portalContainer) {
        return /*#__PURE__*/ createPortal(drawerContent, portalContainer);
    }
    return drawerContent;
};
/**
 * ToolbarButton — WCAG-compliant 44×44px touch target with optional
 * keyboard shortcut hint. Large enough for users of all ages.
 */ function ToolbarButton({ onClick, disabled, active, label, shortcut, children }) {
    const title = shortcut ? `${label} (${shortcut})` : label;
    return /*#__PURE__*/ _jsxs("button", {
        type: "button",
        onClick: onClick,
        disabled: disabled,
        "aria-label": label,
        title: title,
        className: `group relative flex h-11 min-w-[44px] items-center justify-center rounded-xl px-2.5 transition-all ${active ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200' : disabled ? 'cursor-not-allowed text-slate-300' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 active:scale-95 active:bg-slate-200'}`,
        children: [
            children,
            shortcut && !disabled && /*#__PURE__*/ _jsx("span", {
                className: "absolute -bottom-5 left-1/2 hidden -translate-x-1/2 rounded bg-slate-800 px-1.5 py-0.5 text-[9px] font-mono font-medium text-slate-300 shadow-sm group-hover:block",
                children: shortcut
            })
        ]
    });
}
